import fr from "date-fns/locale/fr";
import classnames from "classnames";
import DatePicker, { registerLocale } from "react-datepicker";
import React, { useEffect, useState, forwardRef } from "react";
import { IconCalendarDay } from "../Icon/Icon.tsx";
import Button from "../Button/Button.tsx";
import { DateRange } from "./DateTimeLabel.tsx";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateSelect.css";

const DateSelect = ({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onChangeHandler,
  className,
  selectsRange = true,
  minDate = undefined,
  maxDate = undefined,
  defaultMessage = undefined,
  disabled = false,
  required = false,
}: DateSelectType) => {
  registerLocale("fr", fr);

  const [hasChanged, setHasChanged] = useState(false);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const onChange = (dates) => {
    if (selectsRange) {
      const [startDate, endDate] = dates;
      setHasChanged(true);
      setStartDate(startDate);
      setEndDate(endDate);
    } else {
      setHasChanged(true);
      setStartDate(dates);
    }
  };

  useEffect(() => {
    if (initialStartDate?.getTime() !== startDate?.getTime()) {
      setStartDate(initialStartDate);
    }
  }, [initialStartDate]);

  useEffect(() => {
    if (initialEndDate?.getTime() !== endDate?.getTime()) {
      setEndDate(initialEndDate);
    }
  }, [initialEndDate]);

  const [ref, setRef] = useState(false);

  const closeCalendar = () => {
    ref.setOpen(false);
  };
  const onClose = () => {
    if (!hasChanged) {
      return;
    }
    setHasChanged(false);
    onChangeHandler(startDate, endDate);
  };
  // eslint-disable-next-line react/display-name
  const DateSelectInput = forwardRef(
    ({ onClick }: DateSelectInputType, ref) => (
      <div className={className}>
        <Button
          onClick={onClick}
          ref={ref}
          className={
            startDate || endDate
              ? classnames(styles.select, styles.before)
              : styles.button
          }
          disabled={disabled}
        >
          <IconCalendarDay />
          &nbsp;
          <DateRange
            start={startDate}
            end={endDate}
            defaultMessage={defaultMessage}
          />
        </Button>
        {(startDate || endDate) && (
          <Button
            onClick={() => {
              setStartDate(undefined);
              setEndDate(undefined);
              onChangeHandler(undefined, undefined);
            }}
            className={styles.delete}
            disabled={disabled}
          >
            &#10005;
          </Button>
        )}
      </div>
    ),
  );
  type DateSelectInputType = {
    onClick?: () => void;
  };

  return (
    <div className={styles.align}>
      <DatePicker
        ref={(r) => {
          setRef(r);
        }}
        locale="fr"
        todayButton="Aujourd'hui"
        onChange={onChange}
        customInput={<DateSelectInput />}
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        shouldCloseOnSelect={false}
        onCalendarClose={onClose}
        selectsRange={selectsRange}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        popperClassName={styles.picker}
      >
        <Button
          className={styles.appliquer}
          variant={"primary"}
          onClick={closeCalendar}
        >
          Appliquer
        </Button>
      </DatePicker>
      {required && <span className={styles.obligatoire}>*</span>}
    </div>
  );
};

type DateSelectType = {
  startDate?: Date;
  endDate?: Date;
  onChangeHandler: (e: Date, e2: Date) => void;
  className?: string;
  selectsRange?: boolean;
  minDate?: Date;
  maxDate?: Date;
  defaultMessage?: string;
  disabled?: boolean;
  required?: boolean;
};

export default DateSelect;
